import { spawn } from 'node:child_process';
import type { PreviewEnvironmentProcessLifecycle } from '../process-lifecycle.js';
import type { ShellSpawnResult } from './types.js';
import { devNullReadFd, isSpawnEbadf, mergePreviewEnvironmentSpawnOptions, pipedStdoutStderrFor } from './utils.js';

type Attempt = { stdio: import('node:child_process').StdioOptions; endStdin: boolean };

export type TrySpawnPipedViaShResult =
  | { ok: true; result: ShellSpawnResult }
  | { ok: false; lastErr: unknown };

/**
 * `/bin/sh -c` (or Windows cmd) with piped stdout/stderr — preferred in-memory capture when uv_spawn allows.
 */
export function trySpawnPipedViaSh(
  command: string,
  env: NodeJS.ProcessEnv,
  cwd: string,
  signal?: AbortSignal,
  lifecycle?: PreviewEnvironmentProcessLifecycle,
): TrySpawnPipedViaShResult {
  const attempts: Attempt[] = [
    { stdio: [devNullReadFd(), 'pipe', 'pipe'], endStdin: false },
    { stdio: ['ignore', 'pipe', 'pipe'], endStdin: true },
    { stdio: ['pipe', 'pipe', 'pipe'], endStdin: true },
  ];

  let lastErr: unknown;
  for (let i = 0; i < attempts.length; i++) {
    const attempt = attempts[i]!;
    const opts = mergePreviewEnvironmentSpawnOptions(
      {
        env,
        cwd,
        stdio: attempt.stdio,
        ...(signal ? { signal } : {}),
      },
      lifecycle,
    );
    try {
      let proc: import('node:child_process').ChildProcess;
      if (process.platform === 'win32') {
        opts.windowsHide = true;
        const com = process.env.ComSpec || 'cmd.exe';
        proc = spawn(com, ['/d', '/s', '/c', command], opts);
      } else {
        proc = spawn('/bin/sh', ['-c', command], opts);
      }
      if (attempt.endStdin) {
        proc.stdin?.end();
      }
      return {
        ok: true,
        result: { proc, pipedStdoutStderr: pipedStdoutStderrFor(attempt.stdio) },
      };
    } catch (e) {
      lastErr = e;
      if (!isSpawnEbadf(e)) throw e;
      if (i < attempts.length - 1) continue;
      break;
    }
  }

  return { ok: false, lastErr };
}
